// O que os laudos de imagem dizem, em português de quem não é médico.
//
// Nasceu em 19/09, quando chegaram oito PDFs de tomografia de uma vez. O texto
// do radiologista é preciso e é para outro médico: "opacidades fibroatelectásicas
// nas porções posteriores dos lobos inferiores" não diz nada a quem vai ler no
// corredor — e o que ela significa (parte do pulmão fechada por ficar deitada)
// muda o que a família pergunta na visita.
//
// A REGRA AQUI É A MESMA DO GLOSSÁRIO DE EXAMES: explicar o que o achado é, não
// dizer o que fazer. Diagnóstico e conduta são da equipe.
//
// O segundo uso é a linha do tempo. Cada achado sabe se reconhecer no texto do
// laudo, e sabe reconhecer quando o laudo diz que ele NÃO está ali ("ausência
// de derrame pleural significativo"). Com isso, quatro tomografias de tórax em
// datas diferentes viram uma tabela que mostra o que mudou — que é a pergunta
// que a família realmente tem e que nenhum laudo isolado responde.

export type RegiaoDeImagem = 'torax' | 'cranio' | 'coracao';

export interface AchadoDeImagem {
  chave: string;
  /** Como aparece na tabela, em palavras de quem lê. */
  rotulo: string;
  regiao: RegiaoDeImagem;
  /** O que é, sem dizer o que fazer. */
  oQueE: string;
  /** Por que muda alguma coisa para quem acompanha. Opcional. */
  porQueImporta?: string;
  /** Como o achado aparece escrito no laudo. */
  termos: RegExp[];
}

/**
 * As palavras com que um laudo NEGA um achado.
 *
 * "Ausência de derrame pleural significativo" tem a palavra "derrame" e diz
 * exatamente o contrário. Sem isto, a linha do tempo mostraria derrame em todos
 * os exames — inclusive naquele em que ele finalmente sumiu, que é a única
 * linha que a família quer ver.
 */
/**
 * As palavras com que um laudo NEGA um achado.
 *
 * "Ausência de derrame pleural significativo" tem a palavra "derrame" e diz
 * exatamente o contrário. Sem isto, a linha do tempo mostraria derrame em todos
 * os exames — inclusive naquele em que ele finalmente sumiu, que é a única
 * linha que a família quer ver.
 *
 * Escrita como regex LITERAL, nunca montada a partir de string: dentro de uma
 * string, `\s` vira apenas a letra s, e a negação passaria a casar com quase
 * tudo — silenciosamente, que é o pior jeito de errar aqui.
 */
const NEGACAO =
  /(?<![\wÀ-ÿ])(aus[êe]ncia|n[ãa]o\s+(?:h[áa]|se\s+(?:identificam?|observam?|evidenciam?)|foram|existem?)|sem|isenta\s+de)(?![\wÀ-ÿ])/i;

/**
 * O que vira a frase do avesso depois da negação.
 *
 * "Não há sangramento, MAS HÁ edema" nega a primeira coisa e afirma a segunda.
 * Encontrado isto entre a negação e o termo, a negação não vale mais.
 */
// As bordas sao lookarounds, nao ``: em JavaScript sem a flag `u`, `` nao
// enxerga letra acentuada como letra — e "ha" nunca casava com "há".
// Pior: "sem" sem borda casava dentro de "enfiSEMa" e negava a frase toda.
const VIRA_O_AVESSO =
  /(?<![\wÀ-ÿ])(mas|por[ée]m|contudo|entretanto|todavia|embora|exceto|salvo|h[áa]|existem?|nota-se|observa-se)(?![\wÀ-ÿ])/i;

export const ACHADOS_DE_IMAGEM: AchadoDeImagem[] = [
  // ------------------------------------------------------------- tórax
  {
    chave: 'derrame_pleural',
    rotulo: 'Líquido em volta do pulmão',
    regiao: 'torax',
    oQueE: 'Chamado de derrame pleural. É líquido acumulado entre o pulmão e a '
      + 'parede do peito, no espaço onde normalmente há só uma película fina.',
    porQueImporta: 'Ocupa o lugar do ar e aperta o pulmão por fora, então pode atrapalhar '
      + 'a respiração. É um dos achados que mais mudam de um exame para o outro, e por isso '
      + 'vale acompanhar se está aumentando ou diminuindo.',
    termos: [/derrame\s+pleural/i],
  },
  {
    chave: 'atelectasia',
    rotulo: 'Parte do pulmão fechada',
    regiao: 'torax',
    oQueE: 'Chamada de atelectasia. É uma região do pulmão que murchou e deixou de '
      + 'encher de ar — como um balão que desinflou numa parte só.',
    porQueImporta: 'Em quem está muito tempo deitado, costuma ser efeito da posição e do '
      + 'pouco movimento, não de uma doença nova. Fisioterapia respiratória e mudança de '
      + 'posição são o assunto dela.',
    termos: [/atelectasi/i, /fibroatelect/i],
  },
  {
    chave: 'opacidade_decubito',
    rotulo: 'Manchas de ficar deitada',
    regiao: 'torax',
    oQueE: 'O laudo chama de "opacidades relacionadas ao decúbito": áreas mais densas '
      + 'nas partes de trás e de baixo dos pulmões, que é onde o peso do corpo comprime '
      + 'quem passa o dia na cama.',
    porQueImporta: 'O próprio laudo atribui à posição, e não a infecção. É a diferença '
      + 'entre "está com pneumonia" e "está deitada há semanas".',
    termos: [/relacionad[ao]s?\s+ao\s+dec[úu]bito/i, /opacidades?\s+pulmonares?\s+posteriores/i],
  },
  {
    chave: 'consolidacao',
    rotulo: 'Área do pulmão preenchida',
    regiao: 'torax',
    oQueE: 'Chamada de consolidação. É uma parte do pulmão onde o ar foi substituído '
      + 'por líquido, pus ou células — o pulmão fica "sólido" naquele pedaço.',
    porQueImporta: 'É o achado que costuma acompanhar pneumonia, mas o laudo de imagem '
      + 'sozinho não diz a causa: quem junta isso com febre, exames e exame físico é a equipe.',
    termos: [/consolidati[vf]/i, /consolida[çc][ãa]o/i],
  },
  {
    chave: 'enfisema',
    rotulo: 'Alvéolos alargados (enfisema)',
    regiao: 'torax',
    oQueE: 'Enfisema centrolobular: as bolsinhas de ar do pulmão perderam parede e '
      + 'ficaram maiores e menos elásticas.',
    porQueImporta: 'É uma alteração antiga e permanente, não algo que apareceu nesta '
      + 'internação. Aparece em exame de rotina de muita gente idosa.',
    termos: [/enfisema/i],
  },
  {
    chave: 'cardiomegalia',
    rotulo: 'Coração aumentado',
    regiao: 'torax',
    oQueE: 'Cardiomegalia, ou "aumento da área cardíaca": o coração ocupa mais espaço '
      + 'no peito do que o esperado.',
    porQueImporta: 'É um achado da doença do coração dela, e não uma novidade de cada '
      + 'exame. Aparece em todas as tomografias de tórax desde a entrada.',
    termos: [/cardiomegalia/i, /aumento\s+da\s+[áa]rea\s+card[íi]aca/i],
  },
  {
    chave: 'ateromatose_aorta',
    rotulo: 'Placas na aorta',
    regiao: 'torax',
    oQueE: 'Ateromatose aórtica: depósitos de gordura e cálcio na parede da aorta, '
      + 'a artéria principal do corpo.',
    porQueImporta: 'É desgaste de anos, comum na idade dela. Não é algo que apareceu '
      + 'na internação.',
    termos: [/ateromatose\s+a[óo]rtica/i, /calcifica[çc][õo]es?\s+parietais\s+na\s+aorta/i],
  },
  {
    chave: 'marcapasso_torax',
    rotulo: 'Marca-passo visível',
    regiao: 'torax',
    oQueE: 'Os fios do marca-passo aparecem na imagem — o laudo os chama de "fios '
      + 'metálicos de eletroestimulação" ou cita o marca-passo diretamente.',
    porQueImporta: 'Confirma, no exame, que o dispositivo está no lugar. E é o motivo '
      + 'do alerta que abre o relatório: exame de imagem em portador de marca-passo tem '
      + 'regra própria.',
    termos: [/eletroestimula[çc][ãa]o/i, /marcapasso/i, /marca-passo/i],
  },
  {
    chave: 'tubo_traqueal',
    rotulo: 'Tubo na traqueia',
    regiao: 'torax',
    oQueE: 'O tubo da respiração artificial, colocado pela boca até a traqueia. O laudo '
      + 'diz se ele está bem posicionado.',
    porQueImporta: 'Aparecer ou deixar de aparecer entre um exame e outro marca o dia '
      + 'em que ela saiu do respirador.',
    termos: [/tubo\s+(?:oro)?traqueal/i],
  },
  {
    chave: 'sonda',
    rotulo: 'Sonda de alimentação',
    regiao: 'torax',
    oQueE: 'A sonda que leva o alimento até o estômago. O laudo confere onde a ponta '
      + 'dela parou.',
    porQueImporta: 'A ponta no lugar certo é o que garante que a dieta vai para o '
      + 'estômago, e não para o pulmão.',
    termos: [/sonda\s+(?:digestiva|enteral|nasoenteral)/i],
  },

  // ------------------------------------------------------------ crânio
  {
    chave: 'sangramento',
    rotulo: 'Sangramento no cérebro',
    regiao: 'cranio',
    oQueE: 'Sangue dentro do crânio, seja dentro do cérebro ou em volta dele.',
    porQueImporta: 'É a primeira coisa que uma tomografia de crânio procura depois de '
      + 'uma parada cardíaca, de uma queda ou de uma convulsão. Ausente em todas as dela.',
    termos: [/altera[çc][õo]es\s+hemorr[áa]gicas/i, /hemorragi/i, /cole[çc][õo]es\s+extra-axiais/i,
      /cole[çc][õo]es\s+l[íi]quidas\s+extra-axiais/i],
  },
  {
    chave: 'massa',
    rotulo: 'Massa ou tumor',
    regiao: 'cranio',
    oQueE: 'O laudo chama de "processo expansivo": qualquer coisa que ocupe espaço '
      + 'dentro do crânio e empurre o resto.',
    porQueImporta: 'Ausente em todas as tomografias dela.',
    termos: [/processo\s+expansivo/i],
  },
  {
    chave: 'desvio_linha_media',
    rotulo: 'Desvio do meio do cérebro',
    regiao: 'cranio',
    oQueE: 'Quando algo empurra o cérebro para um lado, a linha que divide os dois '
      + 'hemisférios sai do lugar.',
    porQueImporta: 'É sinal de pressão dentro do crânio. Ausente em todas as dela.',
    termos: [/desvios?\s+(?:significativos?\s+)?(?:das\s+estruturas\s+)?da\s+linha\s+mediana/i,
      /linha\s+m[ée]dia/i],
  },
  {
    chave: 'microangiopatia',
    rotulo: 'Alteração de vasos pequenos',
    regiao: 'cranio',
    oQueE: 'O laudo chama de gliose ou microangiopatia, e descreve como '
      + '"hipoatenuação da substância branca": marcas deixadas nos vasos finos do cérebro '
      + 'ao longo dos anos.',
    porQueImporta: 'É achado comum em pessoas idosas e costuma vir de pressão alta e '
      + 'do tempo. Não é lesão nova nem sequela recente.',
    termos: [/gliose/i, /microangiopatia/i, /hipoatenua[çc][ãa]o\s+da\s+subst[âa]ncia\s+branca/i],
  },
  {
    chave: 'reducao_volume',
    rotulo: 'Redução do volume do cérebro',
    regiao: 'cranio',
    oQueE: 'O cérebro ocupa menos espaço do que na juventude, e os sulcos entre as '
      + 'dobras ficam mais largos. O laudo costuma dizer "habitual para a faixa etária".',
    porQueImporta: 'O próprio laudo classifica como esperado para a idade dela.',
    termos: [/redu[çc][ãa]o\s+(?:do\s+)?volum[ée]trica?\s+encef[áa]lica/i,
      /redu[çc][ãa]o\s+do\s+volume\s+encef[áa]lico/i,
      /proemin[êe]ncia\s+d[oa]s\s+(?:sulcos|fissuras)/i],
  },
  {
    chave: 'ateromatose_carotida',
    rotulo: 'Placas nas artérias do pescoço e do crânio',
    regiao: 'cranio',
    oQueE: 'Ateromatose carotídea e vertebral: depósitos de cálcio nas artérias que '
      + 'levam sangue ao cérebro.',
    porQueImporta: 'Mesmo processo das placas na aorta, e também de anos. Aparece em '
      + 'todas as tomografias dela.',
    termos: [/ateromatose\s+(?:caroti|caroti?dea|carot[íi]dea)/i, /calcifica[çc][õo]es\s+ateromatosas/i,
      /calcifica[çc][õo]es\s+parietais\s+nas\s+por[çc][õo]es\s+cavernosas/i],
  },
  {
    chave: 'dilatacao_ventricular',
    rotulo: 'Ventrículos um pouco maiores',
    regiao: 'cranio',
    oQueE: 'Os espaços com líquido dentro do cérebro ficam um pouco mais largos quando '
      + 'o cérebro reduz de volume. O laudo diz "dilatação compensatória" ou '
      + '"não hipertensiva".',
    porQueImporta: '"Compensatória" e "não hipertensiva" querem dizer que o líquido não '
      + 'está sob pressão — é o espaço que sobrou, não algo empurrando.',
    termos: [/dilata[çc][ãa]o\s+(?:compensat[óo]ria|n[ãa]o\s+hipertensiva)/i],
  },

  // ---------------------------------------------------------- coração
  {
    chave: 'coronarias_limpas',
    rotulo: 'Artérias do coração sem obstrução',
    regiao: 'coracao',
    oQueE: 'O cateterismo entra com contraste nas artérias do coração e filma o caminho '
      + 'do sangue. "Isenta de lesões obstrutivas significativas" quer dizer que nenhuma '
      + 'delas está entupida.',
    porQueImporta: 'Separa duas causas possíveis para um coração fraco: entupimento de '
      + 'artéria (infarto) ou doença do próprio músculo. O cateterismo dela afastou a primeira.',
    termos: [/isenta\s+de\s+les[õo]es\s+obstrutivas/i, /sem\s+les[õo]es\s+obstrutivas/i],
  },
];

const PORCHAVE = new Map(ACHADOS_DE_IMAGEM.map((a) => [a.chave, a]));
export const acharAchado = (chave: string) => PORCHAVE.get(chave);

export type PresencaDeAchado = 'presente' | 'ausente';

export interface AchadoNoLaudo {
  chave: string;
  presenca: PresencaDeAchado;
}

/**
 * Lê um laudo e devolve o que ele afirma e o que ele nega.
 *
 * O que o laudo não menciona não aparece: silêncio não é ausência. Um laudo de
 * tórax que não fala do crânio não está dizendo que o crânio está bom.
 */
export function lerLaudoDeImagem(texto: string): AchadoNoLaudo[] {
  const achados: AchadoNoLaudo[] = [];

  for (const achado of ACHADOS_DE_IMAGEM) {
    let presenca: PresencaDeAchado | null = null;

    for (const termo of achado.termos) {
      const busca = new RegExp(termo.source, termo.flags.includes('g') ? termo.flags : `${termo.flags}g`);
      let m: RegExpExecArray | null;
      while ((m = busca.exec(texto)) !== null) {
        // Olha a FRASE inteira até o termo, não uma janela de caracteres.
        //
        // Uma janela curta não alcança "Não há evidência de processo expansivo
        // intracraniano, BEM COMO DE coleções líquidas extra-axiais" — e o
        // documento passou a anunciar "apareceu sangramento no cérebro" num
        // laudo que diz o contrário. Num papel sobre a mãe de alguém, esse erro
        // não tem desculpa: a frase inteira é o que decide.
        const inicioDaFrase = texto.lastIndexOf('.', m.index - 1) + 1;
        const antes = texto.slice(inicioDaFrase, m.index).replace(/\s+/g, ' ');
        const negacao = NEGACAO.exec(antes);
        // A negação só vale se nada ENTRE ela e o termo virar a frase do avesso.
        // O trecho começa depois da própria negação: "não há" contém "há", e
        // testado desde o começo ele anularia a si mesmo.
        const negado = negacao !== null
          && !VIRA_O_AVESSO.test(antes.slice(negacao.index + negacao[0].length));
        // Uma afirmação em qualquer lugar do laudo vale mais que uma negação:
        // "não há derrame à direita; pequeno derrame à esquerda" tem derrame.
        if (!negado) { presenca = 'presente'; break; }
        if (presenca === null) presenca = 'ausente';
      }
      if (presenca === 'presente') break;
    }

    if (presenca) achados.push({ chave: achado.chave, presenca });
  }

  return achados;
}

/** A que região do corpo o exame pertence, pelo nome dele. */
export function regiaoDoExame(nome: string): RegiaoDeImagem | null {
  const n = nome.toLowerCase();
  if (/(t[óo]rax|pulm[ãa]o|pulmonar)/.test(n)) return 'torax';
  if (/(cr[âa]nio|cerebral|encef)/.test(n)) return 'cranio';
  if (/(cateterismo|cate|coronariografia|cineangio)/.test(n)) return 'coracao';
  return null;
}

// --------------------------------------------------------------- contexto
//
// O que a literatura diz sobre cada achado, para o documento poder explicar a
// linha do tempo com lastro e não com opinião. Cada trecho traz a fonte, e a
// fonte vai impressa: quem lê pode conferir, e quem discorda sabe com quem.
//
// A regra continua a mesma: contexto, nunca conduta. "Efusão em insuficiência
// cardíaca costuma responder a diurético" é o que a literatura diz; "reduzir o
// diurético" é decisão de quem prescreve.

export interface FonteDeLeitura {
  titulo: string;
  url: string;
}

export interface ContextoDeAchado {
  chave: string;
  /** O que a literatura diz, em português de quem não é médico. */
  texto: string;
  fontes: FonteDeLeitura[];
}

export const CONTEXTO_DE_IMAGEM: ContextoDeAchado[] = [
  {
    chave: 'derrame_pleural',
    texto: 'Líquido em volta do pulmão aparece em metade ou mais dos pacientes com '
      + 'insuficiência cardíaca aguda, e nesses casos costuma ser dos dois lados ou do '
      + 'lado direito; quando é só do esquerdo, a literatura recomenda pensar também em '
      + 'outras causas. Em insuficiência cardíaca sem dor no peito nem febre, o caminho '
      + 'usual é tratar o coração e observar — o líquido tende a diminuir junto com a '
      + 'sobrecarga. Um derrame que some entre um exame e outro é, em geral, sinal de que '
      + 'o equilíbrio de líquidos melhorou.',
    fontes: [
      { titulo: 'Pleural Effusion in Congestive Heart Failure — CHEST', url: 'https://journal.chestnet.org/article/S0012-3692(16)32070-0/fulltext' },
      { titulo: 'Pleural Effusion — Merck Manual Professional', url: 'https://www.merckmanuals.com/professional/pulmonary-disorders/mediastinal-and-pleural-disorders/pleural-effusion' },
      { titulo: 'Pleural Effusion and Hemodynamics in Advanced Heart Failure — Circulation: Heart Failure', url: 'https://www.ahajournals.org/doi/10.1161/CIRCHEARTFAILURE.123.011253' },
    ],
  },
  {
    chave: 'atelectasia',
    texto: 'Em quem passa semanas deitado, a parte do pulmão que fica embaixo — as costas, '
      + 'as bases — recebe menos ar e tende a murchar. É a chamada atelectasia dependente, '
      + 'e a tomografia a mostra justamente nas regiões posteriores e inferiores. A '
      + 'literatura descreve que mudar de posição, sentar e fisioterapia respiratória '
      + 'reabrem essas áreas, e que derrame pleural em cima do pulmão o comprime e piora o '
      + 'quadro; tirado o líquido, o pulmão volta a expandir.',
    fontes: [
      { titulo: 'Atelectasis — StatPearls (NIH)', url: 'https://www.ncbi.nlm.nih.gov/books/NBK545316/' },
      { titulo: 'Atelectasis Imaging — Medscape', url: 'https://emedicine.medscape.com/article/353833-overview' },
      { titulo: 'Positioning and rib-cage compression in prolonged ventilation — PMC', url: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC9229462/' },
    ],
  },
  {
    chave: 'opacidade_decubito',
    texto: 'Quando o próprio laudo escreve "relacionadas ao decúbito", o radiologista '
      + 'está dizendo que as manchas têm a distribuição típica de quem está deitado — '
      + 'nas costas e nas bases — e não o padrão de uma pneumonia nova. É a mesma '
      + 'mecânica da atelectasia dependente, vista de outro ângulo.',
    fontes: [
      { titulo: 'Atelectasis — StatPearls (NIH)', url: 'https://www.ncbi.nlm.nih.gov/books/NBK545316/' },
    ],
  },
  {
    chave: 'enfisema',
    texto: 'Enfisema centrolobular é a destruição das paredes dos alvéolos, com '
      + 'predomínio nas partes de cima do pulmão. É a forma mais comum em quem fumou ou '
      + 'respirou fumaça por muitos anos, e é um achado permanente: aparece em qualquer '
      + 'tomografia que se faça, antes e depois da internação.',
    fontes: [
      { titulo: 'Radiograma de tórax e TC na avaliação do enfisema — J Bras Pneumol (SciELO)', url: 'https://www.scielo.br/j/jbpneu/a/TFhhxXZqkdqNq4gChKQm3pS/?lang=pt' },
    ],
  },
  {
    chave: 'cardiomegalia',
    texto: 'Na doença de Chagas, o coração dilata e a fibrose se concentra nas paredes '
      + 'posterior e apical do ventrículo esquerdo e no sistema elétrico — é isso que a '
      + 'Associação Americana do Coração aponta como a marca que distingue a '
      + 'cardiomiopatia chagásica das outras. O coração aumentado na tomografia é o retrato '
      + 'dessa dilatação, e não muda de um exame para o outro.',
    fontes: [
      { titulo: 'Chagas Cardiomyopathy — AHA Scientific Statement (Circulation)', url: 'https://www.ahajournals.org/doi/10.1161/CIR.0000000000000599' },
    ],
  },
  {
    chave: 'coronarias_limpas',
    texto: 'A Associação Americana do Coração descreve a doença de Chagas como uma das '
      + 'principais causas de bloqueio atrioventricular na América Latina, por fibrose do '
      + 'sistema de condução — e o padrão dilatado com coronárias normais é o que a separa '
      + 'da doença por artéria entupida. Um cateterismo sem obstrução, num coração fraco e '
      + 'com bloqueio elétrico, é o quadro clássico da cardiomiopatia chagásica.',
    fontes: [
      { titulo: 'Chagas Cardiomyopathy — AHA Scientific Statement (Circulation)', url: 'https://www.ahajournals.org/doi/10.1161/CIR.0000000000000599' },
      { titulo: 'Electrophysiological characteristics of Chagas disease — PMC', url: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC4878586/' },
    ],
  },
  {
    chave: 'marcapasso_torax',
    texto: 'Por que o marca-passo definitivo espera a infecção acabar: a diretriz '
      + 'americana de infecção em dispositivos cardíacos (atualizada em 2023–2024) '
      + 'recomenda pelo menos 72 horas de hemoculturas negativas antes de implantar um '
      + 'dispositivo permanente — e até 14 dias quando há endocardite valvar — porque um '
      + 'aparelho implantado com bactéria no sangue vira ele mesmo um foco de infecção, '
      + 'difícil de tratar sem retirá-lo.',
    fontes: [
      { titulo: 'Update on CIED Infection Prevention and Management — ACC (2024)', url: 'https://www.acc.org/Latest-in-Cardiology/ten-points-to-remember/2024/01/03/18/33/update-on-cardiovascular-implantable' },
      { titulo: 'Cardiac device infection in patients with bacteremia — PMC', url: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC10402813/' },
    ],
  },
  {
    chave: 'microangiopatia',
    texto: 'A alteração de vasos pequenos do cérebro (leucoaraiose) está presente em cerca '
      + 'de 90% das tomografias de pessoas com mais de 75 anos, segundo levantamentos de '
      + 'grandes populações. É associada à idade e à pressão alta ao longo da vida, aparece '
      + 'devagar ao longo de décadas, e por isso não muda entre um exame e outro na mesma '
      + 'internação. Quando o radiologista escreve "habitual para a faixa etária", é a '
      + 'isso que se refere.',
    fontes: [
      { titulo: 'Cerebral small vessel disease — Radiopaedia', url: 'https://radiopaedia.org/articles/16200/changesets/291526/edits?lang=us' },
      { titulo: 'Leukoaraiosis: epidemiology, imaging, risk factors — Journal of Stroke', url: 'https://www.j-stroke.org/journal/view.php?doi=10.5853%2Fjos.2023.02719' },
      { titulo: 'Age-Related White Matter Changes — PMC', url: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC3163144/' },
    ],
  },
  {
    chave: 'reducao_volume',
    texto: 'Redução do volume do cérebro com sulcos mais largos é o padrão esperado do '
      + 'envelhecimento, e os laudos a classificam como "habitual para a faixa etária". '
      + 'Distingue-se de lesão: é simétrica, sem predomínio de um lobo, e estável.',
    fontes: [
      { titulo: 'Age-Related White Matter Changes — PMC', url: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC3163144/' },
    ],
  },
  {
    chave: 'sangramento',
    texto: 'Depois de parada cardíaca, a tomografia de crânio serve para duas coisas: '
      + 'afastar sangramento ou lesão que mude a conduta, e — nas primeiras 72 horas — '
      + 'estimar a extensão do dano por falta de oxigênio, pela perda do contraste entre '
      + 'substância cinzenta e branca. Tomografias seriadas sem sangramento, sem massa e '
      + 'com o parênquima de atenuação preservada são o resultado que se espera ver quando '
      + 'o cérebro não sofreu lesão estrutural nova.',
    fontes: [
      { titulo: 'Gray-to-white matter ratio on CT after cardiac arrest — meta-analysis (PMC)', url: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC7803139/' },
      { titulo: 'Brain imaging in comatose survivors of cardiac arrest — ScienceDirect', url: 'https://www.sciencedirect.com/science/article/abs/pii/S030095721830889X' },
    ],
  },
  {
    chave: 'massa',
    texto: 'Após uma crise convulsiva num adulto, a diretriz é fazer imagem do cérebro e '
      + 'eletroencefalograma em todos os casos. A imagem procura lesão estrutural; quando '
      + 'ela é normal, o eletroencefalograma continua indicado, porque é ele que mostra a '
      + 'atividade elétrica anormal — e, num estudo, foi a anormalidade focal no EEG que '
      + 'melhor previu lesões que a tomografia não tinha visto. É por isso que, com quatro '
      + 'tomografias sem lesão, o exame que falta é o eletroencefalograma.',
    fontes: [
      { titulo: 'Evaluation After a First Seizure in Adults — American Family Physician (2022)', url: 'https://www.aafp.org/pubs/afp/issues/2022/0500/p507.html' },
      { titulo: 'Imaging Evaluation of the Adult With New-Onset Seizure — AJR', url: 'https://ajronline.org/doi/10.2214/AJR.18.20202' },
      { titulo: 'Interictal epileptiform discharges after first seizure — ILAE/IFCN report (PMC)', url: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC12464601/' },
    ],
  },
];

const CONTEXTO_POR_CHAVE = new Map(CONTEXTO_DE_IMAGEM.map((c) => [c.chave, c]));
export const contextoDoAchado = (chave: string) => CONTEXTO_POR_CHAVE.get(chave);
